"use client";

import {
  type HTMLAttributes,
  useState,
  useRef,
  useEffect,
  type FC,
  type Ref,
} from "react";
import { motion, AnimatePresence } from "motion/react";
import { mergeClasses } from "@huub-web/utils";

import type { ClassNameProp } from "../../props";
import type { IconName } from "../../theme";
import { zIndexes } from "../../theme/zIndex";
import { IconButton } from "../IconButton";
import { FocusTrap } from "../FocusTrap";
import { TargetPositioner } from "../TargetPositioner";
import {
  RadialMenuOption,
  type RadialMenuOptionData,
} from "./components/RadialMenuOption";
import { calculatePosition, calculateRadius } from "./utils/radialMenu.utils";
import { useUuid, useClickOutside } from "../../hooks";

import {
  containerStyle,
  triggerWrapperStyle,
  menuContainerStyle,
} from "./RadialMenu.css";
import { triggerIconVariants } from "./RadialMenu.motion";

export type RadialMenuProps = ClassNameProp &
  Omit<HTMLAttributes<HTMLDivElement>, "onClick"> & {
    /**
     * Icon for the main trigger button
     */
    icon?: IconName;
    /**
     * Array of options to display radially
     */
    options: RadialMenuOptionData[];
    /**
     * Starting angle in degrees (0 = right, -90 = top)
     */
    startAngle?: number;
    /**
     * Total angle spread for options in degrees
     */
    spread?: number;
    /**
     * Whether the menu is disabled
     */
    disabled?: boolean;
    /**
     * Controlled open state (optional)
     */
    open?: boolean;
    /**
     * Callback when menu opens/closes
     */
    onOpenChange?: (open: boolean) => void;
    /**
     * Accessible label for the trigger button
     */
    triggerLabel?: string;
    /**
     * Accessible label for the menu
     */
    menuLabel?: string;
    /**
     * When true, options render at full pill width with labels always
     * visible instead of collapsing to icon-only.
     */
    expanded?: boolean;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  };

/**
 * RadialMenu displays a central icon button that expands to show options
 * arranged in a radial pattern with swoosh animations.
 *
 * Options display as icon-only buttons, and when hovered, turn primary
 * with an animated label sliding in.
 */

export const RadialMenu: FC<RadialMenuProps> = ({
  icon = "add",
  options,
  startAngle = -90,
  spread = 180,
  disabled = false,
  open,
  triggerLabel = "Open menu",
  menuLabel = "Actions menu",
  onOpenChange,
  expanded = false,
  className,
  ref,
  ...props
}) => {
  const menuID = useUuid();
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalIsOpen;
  const [hoveredID, setHoveredID] = useState<string | null>(null);
  const [isHoverEnabled, setIsHoverEnabled] = useState(false);
  const [isVisible, setIsVisible] = useState(isOpen);
  const hoverCountRef = useRef(0);
  const [zIndexMap, setZIndexMap] = useState<Record<string, number>>({});

  const toggleMenu = () => {
    if (disabled) return;

    const newState = !isOpen;

    if (!isControlled) {
      setInternalIsOpen(newState);
    }

    setHoveredID(null);

    if (!newState) {
      hoverCountRef.current = 0;
      setZIndexMap({});
    }

    onOpenChange?.(newState);
  };

  const handleOptionClick = (option: RadialMenuOptionData) => {
    option.onClick?.();

    if (!isControlled) {
      setInternalIsOpen(false);
    }

    setHoveredID(null);
    onOpenChange?.(false);
  };

  const handleHoverStart = (id: string) => {
    if (!isHoverEnabled) return;
    hoverCountRef.current += 1;
    setHoveredID(id);
    setZIndexMap((prev) => ({ ...prev, [id]: hoverCountRef.current }));
  };

  const handleHoverEnd = () => {
    setHoveredID(null);
  };

  const closeMenu = () => {
    if (!isControlled) {
      setInternalIsOpen(false);
    }

    setHoveredID(null);
    hoverCountRef.current = 0;
    setZIndexMap({});
    onOpenChange?.(false);
  };

  /**
   * Enable hover interactions after animation completes (100ms delay)
   */
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        setIsHoverEnabled(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setIsHoverEnabled(false);
    }
  }, [isOpen]);

  /**
   * Keep portal visible during exit animation (200ms delay after close)
   */
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleClickOutside = () => {
    if (isOpen) {
      closeMenu();
    }
  };

  /**
   * Close menu when clicking outside of trigger and menu options
   */
  useClickOutside([triggerRef, menuRef], handleClickOutside);

  /**
   * Render the menu options content for the portal
   */
  const renderMenuContent = () => {
    const radius = calculateRadius({
      optionCount: options.length,
      spread,
    });

    return options.map((option, index) => {
      const position = calculatePosition({
        index,
        total: options.length,
        radius,
        startAngle,
        spread,
      });

      return (
        <RadialMenuOption
          key={option.id}
          option={option}
          isHovered={hoveredID === option.id}
          zIndex={zIndexMap[option.id] ?? 0}
          expanded={expanded}
          onHoverStart={() => handleHoverStart(option.id)}
          onHoverEnd={handleHoverEnd}
          onClick={() => handleOptionClick(option)}
          position={position}
          index={index}
          total={options.length}
        />
      );
    });
  };

  return (
    <div
      ref={ref}
      className={mergeClasses(containerStyle, className)}
      {...props}
    >
      {/* Main trigger button */}
      <div ref={triggerRef} className={triggerWrapperStyle}>
        <motion.div
          variants={triggerIconVariants}
          initial="closed"
          animate={isOpen ? "open" : "closed"}
        >
          <IconButton
            icon={icon}
            variant="backgroundSecondary"
            size="s"
            rounded
            border={isOpen ? "primary" : "dashed"}
            onClick={toggleMenu}
            disabled={disabled}
            active={isOpen}
            aria-label={triggerLabel}
            aria-expanded={isOpen}
            aria-haspopup="menu"
            aria-controls={menuID}
          />
        </motion.div>
      </div>

      {/* Options rendered via TargetPositioner in a portal */}
      <TargetPositioner
        targetRef={triggerRef}
        placement="center"
        active={isVisible}
        mode="fixed"
        zIndex={zIndexes.popup}
        content={
          <AnimatePresence>
            {isOpen && !disabled && (
              <FocusTrap active={isOpen} onEscape={closeMenu} mode="after">
                <div
                  ref={menuRef}
                  id={menuID}
                  role="menu"
                  aria-label={menuLabel}
                  className={menuContainerStyle}
                  style={{ pointerEvents: isHoverEnabled ? "auto" : "none" }}
                >
                  {renderMenuContent()}
                </div>
              </FocusTrap>
            )}
          </AnimatePresence>
        }
      />
    </div>
  );
};
